import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate, Easing } from "remotion";
import { COLORS, FONT, FONT_MONO, NEUTRAL, SAFE } from "../theme";
import { Kicker, useReveal } from "../components";

/* Pantalla de trazabilidad (expediente del vehículo): cada movimiento con
   etapa, fecha y hora exacta, conductor y ubicación. El evento "Salida de
   Puerto → PDI" se resalta como en curso. */

const clamp = { extrapolateLeft: "clamp", extrapolateRight: "clamp" } as const;
const ease = Easing.bezier(0.16, 1, 0.3, 1);

type Ev = { etapa: string; fecha: string; hora: string; chofer: string; ubic: string; live?: boolean; done?: boolean };
const EVENTS: Ev[] = [
  { etapa: "Recepción en Puerto", fecha: "12 ago 2026", hora: "08:15", chofer: "J. Andrade", ubic: "Puerto de Guayaquil", done: true },
  { etapa: "Salida de Puerto → PDI", fecha: "12 ago 2026", hora: "09:42", chofer: "M. Torres", ubic: "Vía Guayaquil–Durán", live: true },
  { etapa: "Ingreso a PDI (taller)", fecha: "12 ago 2026", hora: "10:20", chofer: "M. Torres", ubic: "Centro PDI", },
  { etapa: "Salida a Concesionario", fecha: "—", hora: "—", chofer: "—", ubic: "Pendiente" },
];

const Row: React.FC<{ e: Ev; start: number }> = ({ e, start }) => {
  const r = useReveal(start, 16);
  const bg = e.live ? `rgba(95,160,228,0.12)` : "transparent";
  const border = e.live ? `${COLORS.blueLight}` : NEUTRAL.line;
  const dot = e.done ? COLORS.greenLight : e.live ? COLORS.blueLight : COLORS.sub;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "40px 1.6fr 1fr 1fr 1.2fr", gap: 18, alignItems: "center", padding: "18px 20px", background: bg, borderLeft: `3px solid ${border}`, borderBottom: `1px solid ${NEUTRAL.line}`, ...r }}>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ width: 16, height: 16, borderRadius: 16, background: dot, boxShadow: e.live ? `0 0 14px ${COLORS.blueLight}` : "none" }} />
      </div>
      <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 26, color: COLORS.white, display: "flex", alignItems: "center", gap: 10 }}>
        {e.etapa}
        {e.live && <span style={{ fontFamily: FONT, fontWeight: 700, fontSize: 15, color: COLORS.blueLight, background: `${COLORS.blueLight}22`, border: `1px solid ${COLORS.blueLight}55`, borderRadius: 20, padding: "2px 10px" }}>EN CURSO</span>}
      </div>
      <div style={{ fontFamily: FONT_MONO, fontSize: 22, color: COLORS.sub, fontVariantNumeric: "tabular-nums" }}>{e.fecha}<br /><span style={{ color: COLORS.ink, fontWeight: 700 }}>{e.hora}</span></div>
      <div style={{ fontFamily: FONT, fontSize: 23, color: COLORS.ink }}>{e.chofer}</div>
      <div style={{ fontFamily: FONT, fontSize: 22, color: COLORS.sub }}>{e.ubic}</div>
    </div>
  );
};

export const STrazabilidad: React.FC = () => {
  const frame = useCurrentFrame();
  const titleOp = interpolate(frame, [4, 24], [0, 1], clamp);
  const titleY = interpolate(frame, [4, 24], [22, 0], { ...clamp, easing: ease });
  const winIn = interpolate(frame, [10, 32], [0, 1], { ...clamp, easing: ease });

  return (
    <AbsoluteFill style={{ backgroundColor: COLORS.bg, overflow: "hidden" }}>
      <AbsoluteFill style={{ background: "radial-gradient(1100px 620px at 70% 40%, rgba(95,160,228,0.14), transparent 60%), radial-gradient(900px 560px at 30% 90%, rgba(124,92,191,0.12), transparent 60%)" }} />

      {/* título */}
      <div style={{ position: "absolute", left: SAFE, top: SAFE * 0.7, opacity: titleOp, translate: `0px ${titleY}px` }}>
        <div style={{ marginBottom: 12 }}><Kicker>Trazabilidad</Kicker></div>
        <div style={{ fontFamily: FONT, fontWeight: 800, fontSize: 60, color: COLORS.white, letterSpacing: -1.2, lineHeight: 1.05 }}>
          Cada movimiento, con fecha, hora y responsable
        </div>
      </div>

      {/* ventana / expediente */}
      <div style={{ position: "absolute", left: SAFE, right: SAFE, top: 330, opacity: winIn, translate: `0px ${(1 - winIn) * 28}px` }}>
        <div style={{ background: "#0E1A2B", border: `1px solid ${NEUTRAL.lineStrong}`, borderRadius: 18, overflow: "hidden", boxShadow: "0 40px 100px rgba(0,0,0,0.5)" }}>
          {/* barra de ventana */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 22px", background: "#122236", borderBottom: `1px solid ${NEUTRAL.line}` }}>
            <div style={{ width: 13, height: 13, borderRadius: 13, background: "#E05A45" }} />
            <div style={{ width: 13, height: 13, borderRadius: 13, background: COLORS.amber }} />
            <div style={{ width: 13, height: 13, borderRadius: 13, background: COLORS.greenLight }} />
            <div style={{ fontFamily: FONT, fontWeight: 700, fontSize: 22, color: COLORS.sub, marginLeft: 14 }}>Expediente del vehículo</div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 20 }}>
              <span style={{ fontFamily: FONT_MONO, fontSize: 20, color: COLORS.sub }}>VIN <span style={{ color: COLORS.ink }}>8AJ…70417</span></span>
              <span style={{ fontFamily: FONT_MONO, fontSize: 20, color: COLORS.sub }}>Placa <span style={{ color: COLORS.ink }}>GYE-2410</span></span>
            </div>
          </div>
          {/* encabezado de columnas */}
          <div style={{ display: "grid", gridTemplateColumns: "40px 1.6fr 1fr 1fr 1.2fr", gap: 18, padding: "14px 20px", background: "#0C1626", borderBottom: `1px solid ${NEUTRAL.line}` }}>
            {["", "Movimiento / etapa", "Fecha y hora", "Operador", "Ubicación"].map((h, i) => (
              <div key={i} style={{ fontFamily: FONT, fontWeight: 700, fontSize: 16, letterSpacing: 0.6, textTransform: "uppercase", color: COLORS.sub }}>{h}</div>
            ))}
          </div>
          {/* filas */}
          {EVENTS.map((e, i) => (
            <Row key={i} e={e} start={34 + i * 16} />
          ))}
        </div>
      </div>
    </AbsoluteFill>
  );
};
